import { createSelector, Selector } from '@reduxjs/toolkit'
import { GlobalState } from 'store'
import {
  HaircutEntryDetail,
  EntityType,
  HaircutBand,
  Status,
  Organisation,
  HaircutEntryUpdate,
  ProductPermission,
  OrganisationType,
  ProductSector,
  Product,
  SectorStatus
} from 'api'
import { sortBy, uniq, orderBy, isNil } from 'lodash-es'
import { createAllEntitiesSelector, createEntityGetterSelector } from './entitySelectors'
import { haircutEntryStoreSelector } from './storeSelectors'
import { isOwnOrganisationGetterSelector } from './adminSelectors'
import {
  createActiveTradingEntitiesByOrganisationIdGetterSelector,
  createActiveTradingEntityIdsByOrganisationIdGetterSelector,
  createTradingEntityWithAliasGetterSelector
} from './tradingEntitySelectors/tradingEntitySelectors'
import { RepositoryEntry } from 'store/repository'
import { ProductHelper, OrganisationHelper, TradingEntityHelper, ProductSectorHelper, TradingEntityWithAliasHelper } from 'model'
import { allKnownProductSectorsGetterSelector } from './productSelectors'
import { notNilFilter, ParametricSelector } from 'utils'

export interface HaircutSelectorProps {
  selectedTradingEntityId?: string | null
  selectedOrganisationId?: string | null
  selectedCounterpartyTradingEntityId?: string | null
  selectedCounterpartyOrganisationId?: string | null
  selectedProductId?: string | null
}

const haircutTradingEntityIdSelector = (_: {}, props: HaircutSelectorProps) => props.selectedTradingEntityId
const haircutOrganisationIdSelector = (_: {}, props: HaircutSelectorProps) => props.selectedOrganisationId
const haircutCounterpartyTradingEntityIdSelector = (_: {}, props: HaircutSelectorProps) => props.selectedCounterpartyTradingEntityId
const haircutCounterpartyOrganisationIdSelector = (_: {}, props: HaircutSelectorProps) => props.selectedCounterpartyOrganisationId
const haircutProductIdSelector = (_: {}, props: HaircutSelectorProps) => props.selectedProductId

export const haircutBandSelector: Selector<GlobalState, HaircutBand[]> = createSelector(
  createAllEntitiesSelector(EntityType.HAIRCUT_BAND),
  haircutBands => sortBy(haircutBands, haircutBand => haircutBand.minDays)
)

const haircutEntryRepositoryEntrySelector: Selector<
  GlobalState,
  RepositoryEntry<HaircutEntryUpdate>[]
> = createSelector(haircutEntryStoreSelector, isOwnOrganisationGetterSelector, (haircutEntryStore, getIsOwnOrganisation) =>
  Object.values(haircutEntryStore).filter(repositoryEntry =>
    getIsOwnOrganisation(repositoryEntry.lastUpdate.entity.responderOrganisationId)
  )
)

export const haircutEntryDetailsSelector: Selector<GlobalState, HaircutEntryDetail[]> = createSelector(
  haircutEntryRepositoryEntrySelector,
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  createTradingEntityWithAliasGetterSelector(),
  createEntityGetterSelector(EntityType.ORGANISATION),
  createEntityGetterSelector(EntityType.PRODUCT),
  createEntityGetterSelector(EntityType.PRODUCT_SECTOR),
  (haircutEntryRepositoryEntries, getTradingEntity, getTradingEntityWithAlias, getOrganisation, getProduct, getProductSector) => {
    const haircutEntryDetails = haircutEntryRepositoryEntries.map(repositoryEntry => {
      const haircutEntry = repositoryEntry.lastUpdate.entity
      const responderTradingEntity = getTradingEntity(haircutEntry.responderTradingEntityId)
      const initiatorTradingEntityDefault = getTradingEntity(haircutEntry.initiatorTradingEntityId)
      const responderOrganisation = getOrganisation(haircutEntry?.responderOrganisationId)
      const initiatorTradingEntity =
        haircutEntry.responderTradingEntityId && haircutEntry.initiatorTradingEntityId
          ? getTradingEntityWithAlias(haircutEntry.responderTradingEntityId, haircutEntry.initiatorTradingEntityId)
          : initiatorTradingEntityDefault
      const initiatorOrganisation = getOrganisation(haircutEntry?.initiatorOrganisationId)
      const product = getProduct(haircutEntry?.productId)
      const productSector = getProductSector(haircutEntry?.productSectorId)
      const availableEvents = repositoryEntry.lastUpdate.availableEvents

      return {
        haircutEntry,
        product,
        productSector,
        responderTradingEntity,
        responderOrganisation,
        initiatorTradingEntity,
        initiatorOrganisation,
        availableEvents
      }
    })

    return sortBy(
      haircutEntryDetails,
      detail => (detail.responderOrganisation ? OrganisationHelper.getName(detail.responderOrganisation) : ''),
      detail => (detail.responderTradingEntity ? TradingEntityHelper.getName(detail.responderTradingEntity) : ''),
      detail => (detail.initiatorOrganisation ? OrganisationHelper.getName(detail.initiatorOrganisation) : ''),
      detail => (detail.initiatorTradingEntity ? TradingEntityWithAliasHelper.getName(detail.initiatorTradingEntity) : ''),
      detail => (detail.product ? ProductHelper.getName(detail.product) : ''),
      detail => (detail.productSector ? ProductSectorHelper.getName(detail.productSector) : ''),
      detail => detail.haircutEntry.collateralMaturityMinYears
    )
  }
)

export const haircutOrganisationIdsSelector: Selector<GlobalState, string[]> = createSelector(
  createAllEntitiesSelector(EntityType.ORGANISATION),
  organisations => {
    const sellSideOrganisations = organisations.filter(organisation => organisation.organisationTypes.includes(OrganisationType.SELL_SIDE))

    return sortBy(sellSideOrganisations, organisation => organisation.legalName).map(organisation => organisation.id)
  }
)

export const haircutCounterpartyOrganisationIdsSelector: ParametricSelector<GlobalState, HaircutSelectorProps, string[]> = createSelector(
  haircutOrganisationIdSelector,
  haircutTradingEntityIdSelector,
  createAllEntitiesSelector(EntityType.TRADING_RELATIONSHIP),
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  createEntityGetterSelector(EntityType.ORGANISATION),
  createActiveTradingEntityIdsByOrganisationIdGetterSelector,
  (organisationId, tradingEntityId, tradingRelationships, tradingEntityGetter, getOrganisation, getTradingEntitiesByOrgId) => {
    const orgTradingEntityIds = !isNil(tradingEntityId)
      ? [tradingEntityId]
      : !isNil(organisationId)
      ? getTradingEntitiesByOrgId(organisationId)
      : []

    const counterpartyOrganisations = tradingRelationships
      .filter(
        tradingRelationship =>
          tradingRelationship.status === Status.APPROVED && orgTradingEntityIds.includes(tradingRelationship.sellTradingEntity)
      )
      .map(tradingRelationship => tradingRelationship.buyTradingEntity)
      .map(counterpartyTradingEntityId => tradingEntityGetter(counterpartyTradingEntityId))
      .map(counterpartyTradingEntity =>
        counterpartyTradingEntity === null ? null : getOrganisation(counterpartyTradingEntity.organisationId)
      )
      .filter(notNilFilter)

    return uniq(
      sortBy(counterpartyOrganisations, (counterpartyOrganisation: Organisation) => counterpartyOrganisation.legalName).map(
        (counterpartyOrganisation: Organisation) => counterpartyOrganisation.id
      )
    )
  }
)

export const haircutCounterpartyTradingEntityIdsSelector: ParametricSelector<GlobalState, HaircutSelectorProps, string[]> = createSelector(
  haircutCounterpartyOrganisationIdSelector,
  createAllEntitiesSelector(EntityType.TRADING_ENTITY),
  (organisationId, tradingEntities) => {
    if (!organisationId) {
      return []
    }

    return sortBy(
      tradingEntities.filter(tradingEntity => tradingEntity.organisationId === organisationId),
      tradingEntity => tradingEntity.publishedName
    ).map(tradingEntity => tradingEntity.id)
  }
)

export const haircutPermissionedProductSectorsSelector: ParametricSelector<
  GlobalState,
  HaircutSelectorProps,
  ProductSector[]
> = createSelector(
  haircutOrganisationIdSelector,
  haircutTradingEntityIdSelector,
  createActiveTradingEntitiesByOrganisationIdGetterSelector,
  createEntityGetterSelector(EntityType.TRADING_ENTITY),
  allKnownProductSectorsGetterSelector,
  (organisationId, tradingEntityId, getTradingEntitiesByOrgId, tradingEntityGetter, productSectors) => {
    const tradingEntity = tradingEntityGetter(tradingEntityId)

    const orgTradingEntities = !isNil(tradingEntity)
      ? [tradingEntity]
      : !isNil(organisationId)
      ? getTradingEntitiesByOrgId(organisationId)
      : []

    const productRights = orgTradingEntities.flatMap(counterparty => counterparty.productRights)

    return productSectors
      .filter(productSector =>
        productRights.some(
          productRight => productRight.productSectorId === productSector.id && productRight.permission === ProductPermission.TRADE
        )
      )
      .filter(productSector => productSector.status !== SectorStatus.DELETED)
  }
)

export const haircutProductSectorIdsSelector: ParametricSelector<GlobalState, HaircutSelectorProps, string[]> = createSelector(
  [haircutPermissionedProductSectorsSelector, haircutProductIdSelector],
  (permissionedProductSectors, productId) => {
    if (productId === null) {
      return []
    }

    return orderBy(
      permissionedProductSectors.filter(productSector => productSector.productId === productId),
      productSector => productSector.name
    ).map(productSector => productSector.id)
  }
)

export const haircutProductIdsSelector: ParametricSelector<GlobalState, HaircutSelectorProps, string[]> = createSelector(
  haircutPermissionedProductSectorsSelector,
  createEntityGetterSelector(EntityType.PRODUCT),
  (permissionedProductSectors, getProduct) => {
    const products = uniq(
      permissionedProductSectors.map(productSector => getProduct(productSector.productId)).filter(product => product !== null)
    ) as Product[]

    return sortBy(products, product => product.name).map(product => product.id)
  }
)

export const haircutCollateralBandIdsSelector: ParametricSelector<GlobalState, HaircutSelectorProps, string[]> = createSelector(
  haircutCounterpartyTradingEntityIdSelector,
  createAllEntitiesSelector(EntityType.HAIRCUT_COLLATERAL_BAND),
  (counterpartyTradingEntityId, haircutCollateralBands) => {
    if (!counterpartyTradingEntityId) {
      return []
    }
    return haircutCollateralBands.map(haircutCollateralBand => haircutCollateralBand.id)
  }
)
